/**
 * @name useGerenciarRestricoesAlimentares
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Hook para gerenciar restrições alimentares dos dependentes
 * @returns Hook com estados e funções para gerenciar restrições
 * @package src/components/pages/gestao-financeira/dependentes/restricoes/useGerenciarRestricoesAlimentares.ts
 */

import { useCallback, useEffect, useState } from 'react';
import { useAlert } from '../../../../../contexts';
import useUserContext from '../../../../../contexts/UserContext/useUserContext';
import { produtosService } from '../../../../../services/supabase/entities/ProdutosService';
import { restricoesAlimentaresService } from '../../../../../services/supabase/entities/RestricoesAlimentaresService';
import { restricoesAlimentaresViewService } from '../../../../../services/supabase/entities/RestricoesAlimentaresViewService';
import { IPessoas } from '../../../../../types/tables/IPessoas';
import { IProdutos } from '../../../../../types/tables/IProdutos';
import { IVwRestricoesAlimentares } from '../../../../../types/views/IVwRestricoesAlimentares';

interface LoadingState {
     geral: boolean;
     adicionando: boolean;
     removendo: number | null;
}

export const useGerenciarRestricoesAlimentares = (dependente: IPessoas) => {
     const { showError } = useAlert();
     const { userData } = useUserContext();
     const organizationId = userData?.fk_id_organizacao;
     const userId = userData?.id;

     // Estados
     const [restricoesList, setRestricoesList] = useState<IVwRestricoesAlimentares[]>([]);
     const [totalRestricoes, setTotalRestricoes] = useState<number>(0);
     const [produtosDisponiveis, setProdutosDisponiveis] = useState<IProdutos[]>([]);
     const [modalAberto, setModalAberto] = useState(false);
     const [loading, setLoading] = useState<LoadingState>({
          geral: false,
          adicionando: false,
          removendo: null,
     });

     /**
      * Busca restrições do dependente
      */
     const buscarRestricoes = useCallback(async () => {
          if (!dependente?.id || !organizationId) return;
          try {
               setLoading(prev => ({ ...prev, geral: true }));
               // Busca restrições do dependente na VIEW para garantir campos extras e atualização instantânea
               const response = await restricoesAlimentaresViewService.getAll(organizationId, 0, 100, undefined, {
                    items: [
                         { field: 'fk_id_aluno', operator: 'equals', value: dependente.id }
                    ]
               });
               if (response?.error) {
                    showError('Erro ao carregar restrições alimentares');
                    return;
               }
               if (response?.data) {
                    setRestricoesList(response.data);
                    setTotalRestricoes(response.data.length);
               }
          } catch (error: any) {
               console.error('Erro ao buscar restrições:', error);
               showError('Erro ao carregar restrições alimentares');
          } finally {
               setLoading(prev => ({ ...prev, geral: false }));
          }
     }, [dependente?.id, organizationId, showError]);

     /**
      * Busca produtos disponíveis
      */
     const buscarProdutos = useCallback(async () => {
          try {
               if (!organizationId) return;
               const response = await produtosService.getAllActive(organizationId);
               if (response.error) {
                    showError('Erro ao carregar produtos disponíveis');
                    return;
               }
               if (response.data) {
                    setProdutosDisponiveis(response.data);
               }
          } catch (error: any) {
               console.error('Erro ao buscar produtos:', error);
               showError('Erro ao carregar produtos disponíveis');
          }
     }, [organizationId, showError]);

     /**
      * Adiciona uma nova restrição
      */
     // A view não permite adicionar restrições diretamente
     /**
      * Adiciona uma nova restrição alimentar para o dependente
      */
     const adicionarRestricao = useCallback(
          async (produtoId: number, observacoes?: string) => {
               if (!organizationId || !dependente?.id) {
                    showError('Dados insuficientes para adicionar restrição.');
                    return;
               }
               setLoading((prev) => ({ ...prev, adicionando: true }));
               try {
                    const response = await restricoesAlimentaresService.create(
                         {
                              fk_id_aluno: dependente.id,
                              fk_id_produto: produtoId,
                              observacoes: observacoes || null,
                              ativo: true,
                         },
                         organizationId,
                         userId
                    );
                    if (response?.error) {
                         showError('Erro ao adicionar restrição alimentar');
                         return;
                    }
                    await buscarRestricoes();
                    setModalAberto(false);
               } catch (error: any) {
                    console.error('Erro ao adicionar restrição:', error);
                    showError('Erro ao adicionar restrição alimentar');
               } finally {
                    setLoading((prev) => ({ ...prev, adicionando: false }));
               }
          },
          [organizationId, dependente?.id, userId, showError, buscarRestricoes]
     );

     /**
      * Remove uma restrição
      */
     // Remove uma restrição pelo ID
     const removerRestricao = useCallback(async (restricaoId: number) => {
          if (!organizationId) {
               showError('Organização não encontrada.');
               return;
          }
          setLoading(prev => ({ ...prev, removendo: restricaoId }));
          try {
               const response = await restricoesAlimentaresService.softDelete(restricaoId, organizationId, userId);
               if (response?.error) {
                    showError('Erro ao remover restrição alimentar');
                    return;
               }
               await buscarRestricoes();
          } catch (error: any) {
               console.error('Erro ao remover restrição:', error);
               showError('Erro ao remover restrição alimentar');
          } finally {
               setLoading(prev => ({ ...prev, removendo: null }));
          }
     }, [buscarRestricoes, showError, organizationId, userId]);

     /**
      * Formata nome do produto da restrição
      */
     const formatarNomeProduto = useCallback((restricao: IVwRestricoesAlimentares): string => {
          return restricao.produto_descricao || `Produto ${restricao.fk_id_produto}`;
     }, []);

     // Carregar dados iniciais
     useEffect(() => {
          buscarRestricoes();
          buscarProdutos();
     // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [dependente.id]);

     return {
          // Estados
          restricoes: restricoesList,
          produtosDisponiveis,
          loading,
          modalAberto,
          totalRestricoes,

          // Ações
          abrirModal: () => setModalAberto(true),
          fecharModal: () => setModalAberto(false),
          adicionarRestricao,
          removerRestricao,
          buscarRestricoes,

          // Utilitários
          formatarNomeProduto,
     };
};
